import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
    Divider, makeStyles, Theme, Typography, Modal, Box,
} from '@material-ui/core';

import moment from 'moment';
import { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete';
import clsx from 'clsx';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Page } from '../../components/Page';
import { BackAppBar } from '../../components/BackAppBar';
import { PageBody } from '../../components/PageBody';
import { FormGroup } from '../../components/FormGroup';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { Block } from '../../components/Block';
import { ProductList } from './components/ProductList';
import { ProductProgressRequestData } from '../../api/productProgress';
import { useAlert } from '../../units/alert';
import { useLoading } from '../../units/loading';
import { Product } from '../../data/Product';
import { api } from '../../api';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { useSetting } from '../../units/setting';
import { usePartner } from '../../hooks/usePartner';
import { Autocomplete } from '../../components/Autocomplete';
import { useProductList } from '../../hooks/useProductList';
import { Checkbox } from '../../components/Checkbox';
import { useProductModelOptions } from '../../hooks/useProductModelOptions';
import { ProductMachineList } from './components/ProductMachineList';
import { useMachineLocationOptions } from '../../hooks/useMachineLocationOptions';
import { usePermission } from '../../hooks/usePermission';

type ProductListPageProps = {};

ProductListPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    modal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        backgroundColor: theme.palette.background.default,
        border: '1px solid #B2B2B2',
        boxShadow: theme.shadows[24],
        [theme.breakpoints.up('md')]: {
            // 電腦版
            width: '50%',
            // padding: theme.spacing(2),
        },
    },
    hidden: {
        display: 'none',
    },
}));

function useFormData() {
    const [formData, setFormData] = useState<ProductProgressRequestData>({
        barcode: '',
        sn_code: '',
        product_model_id: '',
        line_uid: '',
        login: '',
        location: '',
    });

    return {
        formData,
        setFormData,
    };
}

function ProductListPage(props: ProductListPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setting } = useSetting();
    const alert = useAlert();
    const { setIsLoading } = useLoading();
    const { formData, setFormData } = useFormData();
    const [resultProductList, setResultProductList] = useState<Product[]>([]);
    const { partner, partnerLineUid, isLoading: isPartnerLoading } = usePartner();
    const { productList } = useProductList({
        line_uid: partnerLineUid || '',
        login: partner?.email || '',
    });
    const productModelOptions = useProductModelOptions(productList);
    const machineLocationOptions = useMachineLocationOptions({
        line_uid: partnerLineUid || '',
        login: partner?.email || '',
    });
    const [showExpireProduct, setShowExpireProduct] = useState(false);
    const [machineSearchModalOpen, setMachineSearchModalOpen] = React.useState(false);
    const { permission } = usePermission();

    useEffect(() => {
        setIsLoading(isPartnerLoading);
        if (partner) {
            handleSubmitClick();
        }
    }, [partner]);

    function handleMachineSearchClick() {
        setMachineSearchModalOpen(true);
    }

    function handleMachineSearchModalClose() {
        setMachineSearchModalOpen(false);
    }

    function handleSubmitClick() {
        setIsLoading(true);
        setResultProductList([]);
        api.productProgress({
            ...formData,
            login: partner?.email || '',
            line_uid: partnerLineUid || '',
        })
            .then((resData) => {
                setIsLoading(false);
                setResultProductList(resData);
            })
            .catch(async (pickup_pending_qty) => {
                setIsLoading(false);
                if (pickup_pending_qty instanceof StatusFalseError) {
                    const errorData = await pickup_pending_qty.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    function handleProductClick(product: Product) {
        history.push(`/repair?barcode=${product.barcode}&product_model_id=${product.product_model_id}&sn_code=${product.sn_code}`);
    }

    function handleBarcodeChange(value: string, reason?: AutocompleteChangeReason | 'change') {
        setFormData((prevState) => ({
            ...prevState,
            barcode: value,
        }));
    }

    function productListSelector() {
        if (showExpireProduct) {
            return resultProductList.filter((product) => (
                product.date_next !== ''
                    && moment(moment().format('YYYY-MM-DD 00:00:00')).unix() >= moment(product.date_next).unix()
            ));
        }
        return resultProductList;
    }

    return (
        <>
            <Page>
                <BackAppBar title="產品列表" onBackClick={() => history.replace('/service/action')} />
                <PageBody>
                    <FormGroup>
                        <Block p={1} row justifyContent="space-between">
                            <Typography variant="h5">查詢條件</Typography>
                            <Button onClick={handleMachineSearchClick} className={clsx({ [classes.hidden]: permission?.machine_state !== true })} style={{ paddingInline: 14 }}>
                                <span>所有機台狀況</span>
                                <Icon icon="file-search" size="lg" style={{ marginLeft: 8 }} />
                            </Button>
                        </Block>
                        <Autocomplete
                            options={productList.map((item) => ({ text: `${item.barcode} - ${item.product_model_id} - ${item.sn_code}`, value: item.barcode }))}
                            label="產品辨識碼"
                            freeSolo
                            onChange={handleBarcodeChange}
                            value={formData.barcode}
                        />
                        <Autocomplete
                            options={productModelOptions}
                            label="產品型號"
                            freeSolo
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    product_model_id: value,
                                });
                            }}
                            value={formData.product_model_id}
                        />
                        <TextField
                            label="產品序號"
                            value={formData.sn_code}
                            onChange={(pickup_pending_qty) => setFormData({
                                ...formData,
                                sn_code: pickup_pending_qty.target.value,
                            })}
                        />
                        <Autocomplete
                            options={machineLocationOptions}
                            label="上機位置"
                            freeSolo
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    location: value,
                                });
                            }}
                            value={formData.location}
                        />
                        <Button onClick={handleSubmitClick}>查詢資料</Button>
                    </FormGroup>
                    <Divider />
                    <Block p={1} row justifyContent="space-between">
                        <Typography variant="h5">查詢結果</Typography>
                        <Checkbox
                            label="顯示保養日已到期的產品"
                            checked={showExpireProduct}
                            onChange={(event, checked) => setShowExpireProduct(checked)}
                        />
                    </Block>
                    <ProductList
                        productList={productListSelector()}
                        onClick={handleProductClick}
                    />
                </PageBody>
            </Page>
            <Modal
                open={machineSearchModalOpen}
                onClose={handleMachineSearchModalClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box className={classes.modal}>
                    <ProductMachineList login={partner?.email || ''} line_uid={partnerLineUid || ''} />

                </Box>
            </Modal>
        </>

    );
}

export { ProductListPage };
